import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { detectPreferredLanguage } from "util/language";
import { mobilPlatformBul } from "util/mobilPlatform";

/**
 * MOBİL TARAYICIDAN GİRENE UYGULAMA ÖNERİSİ.
 *
 * Telefonun tarayıcısından giren kullanıcı panelin mobil sürümünü kullanıyor;
 * mağaza uygulaması ona bildirim, kamera ve hızlı açılış getiriyor. Ama bu
 * öneri işi bölmemeli:
 *
 *   1) YALNIZ MOBİLDE çıkar — masaüstünde mağaza linki anlamsız.
 *   2) UYGULAMANIN KENDİ İÇİNDE çıkmaz — zaten uygulamadaki birine
 *      "uygulamayı indir" demek saçmadır.
 *   3) KAPATILINCA BİR DAHA ÇIKMAZ. Kullanıcının açık isteği: "isteyen
 *      kapatsın, sürekli kalmasın." Kapatma kararı tarayıcıda saklanır.
 *   4) HEMEN ÇIKMAZ — sayfa otursun diye kısa bir gecikme var; açılışta
 *      üstüne atlayan şerit, kapatılmayı hak eder.
 *   5) İÇERİĞİ ÖRTMEZ — altta durur, sayfanın altına o kadar boşluk
 *      bırakılır ki son satır şeridin altında kalmasın.
 *
 * Uygulama iki mağazada da yayında; adresler sabit. Ortam değişkenine
 * bağlamak bunun gibi hiç değişmeyen iki adres için gereksiz dolambaç
 * olurdu — derleme ortamı eksik kurulduğunda özellik sessizce kaybolur.
 */

const SAKLAMA_ANAHTARI = "cp_uygulama_seridi_kapatildi";
const GECIKME_MS = 2500;

/* ŞERİT SAYFANIN DİLİNİ KONUŞUR. Ölçüldü: tanıtım sayfası İngilizceyken
   şerit Türkçe çıkıyordu — ziyaretçi dili kendi seçmişken ona başka dilde
   seslenmek özensizlik olarak okunur. Panelin dil algısı (util/language)
   ile aynı kaynaktan besleniyor; sözlükte olmayan dil İngilizceye düşer. */
const METINLER = {
  tr: { baslik: "ChatPilots uygulaması", alt: "Bildirimler anında gelsin.", kapat: "Kapat" },
  en: { baslik: "ChatPilots app", alt: "Get notifications instantly.", kapat: "Dismiss" },
  es: { baslik: "App de ChatPilots", alt: "Recibe avisos al instante.", kapat: "Cerrar" },
  ja: { baslik: "ChatPilots アプリ", alt: "通知をすぐに受け取れます。", kapat: "閉じる" },
  zh: { baslik: "ChatPilots 应用", alt: "第一时间收到通知。", kapat: "关闭" },
  pt: { baslik: "App ChatPilots", alt: "Receba avisos na hora.", kapat: "Fechar" },
  ar: { baslik: "تطبيق ChatPilots", alt: "استقبل الإشعارات فورًا.", kapat: "إغلاق" },
  ru: { baslik: "Приложение ChatPilots", alt: "Уведомления приходят сразу.", kapat: "Закрыть" },
  id: { baslik: "Aplikasi ChatPilots", alt: "Terima notifikasi seketika.", kapat: "Tutup" },
  fr: { baslik: "Application ChatPilots", alt: "Recevez les alertes aussitôt.", kapat: "Fermer" },
  de: { baslik: "ChatPilots-App", alt: "Benachrichtigungen sofort erhalten.", kapat: "Schließen" },
  it: { baslik: "App ChatPilots", alt: "Ricevi le notifiche subito.", kapat: "Chiudi" },
  hi: { baslik: "ChatPilots ऐप", alt: "सूचनाएँ तुरंत पाएँ।", kapat: "बंद करें" },
  az: { baslik: "ChatPilots tətbiqi", alt: "Bildirişlər dərhal gəlsin.", kapat: "Bağla" },
};

/* MAĞAZA ADRESLERİ — ikisi de yayında (26.08.2026'da doğrulandı). */
const MAGAZA = {
  ios: { adres: "https://apps.apple.com/app/id6798466301", ad: "App Store" },
  android: { adres: "https://play.google.com/store/apps/details?id=ai.chatpilots.app", ad: "Google Play" },
};

/** iOS / Android / null. Mantık util/mobilPlatform'da — orası sınanıyor. */
export function mobilPlatform() {
  if (typeof navigator === "undefined") return null;
  return mobilPlatformBul(navigator, typeof window !== "undefined" ? window : null);
}

export default function UygulamaIndirmeSeridi({ gizle }) {
  const [gorunur, setGorunur] = useState(false);
  const [platform, setPlatform] = useState(null);
  const [dil, setDil] = useState("en");

  useEffect(() => {
    if (gizle) return undefined;
    const p = mobilPlatform();
    if (!p) return undefined;

    if (!MAGAZA[p]) return undefined;

    let kapatilmis = false;
    try { kapatilmis = window.localStorage.getItem(SAKLAMA_ANAHTARI) === "1"; } catch (e) { kapatilmis = false; }
    if (kapatilmis) return undefined;

    setPlatform(p);
    try { setDil(detectPreferredLanguage()); } catch (e) { setDil("en"); }
    const zaman = setTimeout(() => setGorunur(true), GECIKME_MS);
    return () => clearTimeout(zaman);
  }, [gizle]);

  /* Şerit görünürken sayfanın altına yer aç: son satırın üstüne oturursa
     kullanıcı onu okuyamaz ve şeridi düşman belleyip kapatır. */
  useEffect(() => {
    if (!gorunur) return undefined;
    const govde = document.body;
    const eski = govde.style.paddingBottom;
    govde.style.paddingBottom = "84px";
    return () => { govde.style.paddingBottom = eski; };
  }, [gorunur]);

  if (!gorunur || !platform) return null;

  const { adres, ad: magazaAdi } = MAGAZA[platform];
  const m = METINLER[dil] || METINLER.en;

  const kapat = () => {
    setGorunur(false);
    try { window.localStorage.setItem(SAKLAMA_ANAHTARI, "1"); } catch (e) { /* gizli sekme: bu oturumda yeter */ }
  };

  /* PORTAL + SINIF, SATIR İÇİ STİL DEĞİL.
     Ölçüldü: tanıtım sayfası `#root>div { display:block!important;
     min-height:100vh!important }` basıyor. Satır içi stil `!important`
     kuralını YENEMEZ; şerit blok olup ekranı boydan boya kaplıyor,
     içerik üst üste diziliyordu. Gövdeye portal ile taşınınca sayfaya
     özel kurallar hiç dokunmuyor; kritik özellikler ayrıca sınıf +
     !important ile yazılıyor ki başka bir sayfa da kıramasın. */
  const govde = (
    <div className="cp-uygulama-seridi" role="complementary" aria-label={m.baslik}>
      <style>{`
        .cp-uygulama-seridi {
          position: fixed !important;
          left: 12px !important;
          right: 12px !important;
          bottom: calc(12px + env(safe-area-inset-bottom, 0px)) !important;
          top: auto !important;
          width: auto !important;
          height: auto !important;
          min-height: 0 !important;
          max-height: none !important;
          z-index: 1200 !important;
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
          box-sizing: border-box !important;
          padding: 10px 12px !important;
          border-radius: 16px !important;
          background: var(--cp-surface, #111827) !important;
          border: 1px solid var(--cp-border, rgba(148,163,184,0.22)) !important;
          /* Gölge iki temada da işe yarasın: koyuda derinlik, açıkta
             hafif ayrım. Tek sabit siyah gölge açık zeminde kirli durur. */
          box-shadow: 0 10px 28px rgba(15,23,42,0.22) !important;
          animation: cpSeritGir 320ms cubic-bezier(0.16,1,0.3,1);
        }
        /* Yalnız metin sütunu daralır; ikon ve düğmeler ezilmez. */
        .cp-uygulama-seridi > * { flex-shrink: 0; }
        .cp-uygulama-seridi > .cp-seridi-metin { flex: 1 1 auto !important; min-width: 0 !important; }
        @keyframes cpSeritGir {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cp-uygulama-seridi { animation: none; }
        }
      `}</style>

      <div
        aria-hidden="true"
        style={{
          width: 38,
          height: 38,
          flex: "0 0 38px",
          borderRadius: 10,
          background: "linear-gradient(135deg,#6366f1,#22d3ee)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 19,
        }}
      >
        💬
      </div>

      <div className="cp-seridi-metin" style={{ flex: 1, minWidth: 0 }}>
        {/* Başlık TEK satır kalır: sarınca şerit üç satıra çıkıp ekranın
            altını yiyordu (375px genişlikte ölçüldü). */}
        <div style={{ fontWeight: 700, fontSize: 13.5, color: "var(--cp-text, #e5e7eb)", lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {m.baslik}
        </div>
        <div style={{ fontSize: 11.5, color: "var(--cp-muted, #94a3b8)", lineHeight: 1.35, marginTop: 1 }}>
          {m.alt}
        </div>
      </div>

      <a
        href={adres}
        target="_blank"
        rel="noopener noreferrer"
        onClick={kapat}
        style={{
          flex: "0 0 auto",
          padding: "8px 12px",
          borderRadius: 9,
          background: "linear-gradient(135deg,#6366f1,#22d3ee)",
          color: "#0b1020",
          fontWeight: 700,
          fontSize: 12.5,
          textDecoration: "none",
          whiteSpace: "nowrap",
        }}
      >
        {magazaAdi}
      </a>

      <button
        type="button"
        onClick={kapat}
        aria-label={m.kapat}
        style={{
          flex: "0 0 auto",
          width: 30,
          height: 30,
          borderRadius: 8,
          border: "none",
          background: "transparent",
          color: "var(--cp-muted, #94a3b8)",
          fontSize: 18,
          lineHeight: 1,
          cursor: "pointer",
        }}
      >
        ×
      </button>
    </div>
  );

  return ReactDOM.createPortal(govde, document.body);
}
